import React, { createContext, useContext, useState } from 'react'
import { createStore, useStore } from 'zustand'
import type { StoreApi } from 'zustand'
import type { TranslationFile } from '../../models/translationsType'
import fetchAvailableLanguages from '../../utils/fetchAvailableLanguages'
import { currentTranslatedLocales } from '../../translations/allowedLocales'
import * as translations from '../../translations'
import { deepMerge } from '../../utils/getThemeColors'
import { rtlLanguages } from '../../models/constants'

interface LocaleState {
  locale: string
  defaultLocale: string
  supported: string[]
  dumpedLanguagesFallback: Record<string, string>
  replacementLocales: Record<string, string>
  defaultTranslations: TranslationFile
  currentTranslations: TranslationFile
  isRTL: boolean
  localeForRetorikStudioContent: string
  actions: {
    setLocale: (value: string) => void
    fetchSupportedLanguages: (baseAddress: string) => Promise<void>
    setLanguageData: (defaultLocale: string, supported: Array<string>) => void
    addDumpedLanguageFallback: (key: string, value: string) => void
    addReplacementLocale: (key: string, value: string) => void
    checkDumpedLocaleFallback: () => string
    checkReplacementLocale: (value: string) => string
    resetLocaleStore: () => void
  }
}

const initialState = {
  locale: '',
  defaultLocale: 'fr-FR',
  supported: [],
  dumpedLanguagesFallback: {},
  replacementLocales: {},
  defaultTranslations: structuredClone(translations.fr_FR),
  currentTranslations: structuredClone(translations.fr_FR),
  isRTL: false,
  localeForRetorikStudioContent: ''
}

const createLocaleStore = () =>
  createStore<LocaleState>((set, get) => {
    return {
      ...initialState,
      actions: {
        setLocale: (value: string) => {
          set((state) => {
            const splitLocale = value.split('-')[0]
            const changeLocaleEvent = new CustomEvent('retorikChangeLocale', {
              detail: {
                locale: value
              }
            })
            document.dispatchEvent(changeLocaleEvent)

            const tempTranslation = structuredClone(translations[value.replace('-', '_')])
            let mergedTranslations: any = state.defaultTranslations

            if (tempTranslation) {
              mergedTranslations = deepMerge(tempTranslation, state.defaultTranslations)
            } else {
              // Try to find a fallback translation language of the same locale but different culture
              const fallbackLocale = currentTranslatedLocales.find((l) => l.split('-')[0] === splitLocale)
              if (fallbackLocale) {
                mergedTranslations = deepMerge(structuredClone(translations[fallbackLocale.replace('-', '_')]), state.defaultTranslations)
              }
            }

            return {
              locale: value,
              isRTL: rtlLanguages.includes(splitLocale),
              localeForRetorikStudioContent: state.dumpedLanguagesFallback[value] || value,
              currentTranslations: { ...mergedTranslations }
            }
          })
        },
        fetchSupportedLanguages: async (baseAddress) => {
          fetchAvailableLanguages(baseAddress).then((languages) => {
            set({
              defaultLocale: languages.default,
              supported: languages.all
            })
          })
        },
        setLanguageData: (defaultLocale: string, supported: Array<string>) => {
          set({
            defaultLocale: defaultLocale,
            supported: supported
          })
        },
        addDumpedLanguageFallback: (key: string, value: string) => {
          set((state) => ({
            dumpedLanguagesFallback: { ...state.dumpedLanguagesFallback, [key]: value }
          }))
        },
        addReplacementLocale: (key: string, value: string) => {
          set((state) => ({
            replacementLocales: { ...state.replacementLocales, [key]: value }
          }))
        },
        checkDumpedLocaleFallback: () => {
          if (Object.keys(get().dumpedLanguagesFallback).length) {
            return get().dumpedLanguagesFallback[get().locale] || get().locale
          }

          return get().locale
        },
        checkReplacementLocale: (locale: string) => {
          if (Object.keys(get().replacementLocales).length) {
            return get().replacementLocales[locale] || locale
          }

          return locale
        },
        resetLocaleStore: () => {
          set({
            ...initialState
          })
        }
      }
    }
  })

const LocaleContext = createContext<StoreApi<LocaleState> | null>(null)

const useLocaleStore = <T,>(selector: (state: LocaleState) => T): T => {
  const store = useContext(LocaleContext)
  if (!store) throw new Error('Retorik Framework > useLocaleStore must be used within LocaleProvider')
  return useStore(store, selector)
}

const LocaleProvider = ({ children }) => {
  const [store] = useState(() => createLocaleStore())

  return <LocaleContext.Provider value={store}>{children}</LocaleContext.Provider>
}

export default LocaleProvider
export { useLocaleStore }
